import { useRef, useState } from "react";
import { Alert, Button, Card, Form } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { Container } from "react-bootstrap";
import AppNavBar from '../components/AppNavBar'
import { supabase } from "../supabaseClient";

// Libellé du facteur et nom de l'entrée correspondante dans l'application d'authentification
// (issuer « Hopfone (nom) » pour les facteurs nommés, « Hopfone » pour les plus anciens).
const factorLabel = (factor, index) => factor?.friendly_name || `Facteur ${index + 1}`;
const authenticatorEntry = (factor) => factor?.friendly_name ? `Hopfone (${factor.friendly_name})` : "Hopfone";

const Login = () => {
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  // Second facteur : renseigné après le mot de passe si le compte a la MFA activée.
  const [factors, setFactors] = useState([]);
  const [selectedFactorId, setSelectedFactorId] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const navigate = useNavigate();
  const { login, signOut } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setErrorMsg("");
      setLoading(true);
      if (!passwordRef.current?.value || !emailRef.current?.value) {
        setErrorMsg("Please fill in the fields");
        return;
      }
      const {
        data: { user, session },
        error
      } = await login(emailRef.current.value, passwordRef.current.value);
      if (error) setErrorMsg(error.message);
      if (user && session) {
        // nextLevel aal2 alors que la session est aal1 : un facteur vérifié existe, le code est requis.
        const { data: aal, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalError) throw aalError;
        if (aal.currentLevel === "aal1" && aal.nextLevel === "aal2") {
          const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
          if (factorsError) throw factorsError;
          const totpFactors = factorsData?.totp ?? [];
          setFactors(totpFactors);
          setSelectedFactorId(totpFactors[0]?.id ?? null);
          return;
        }
        navigate("/");
      }
    } catch (error) {
      setErrorMsg("Email or Password Incorrect");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    try {
      setErrorMsg("");
      setLoading(true);
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: selectedFactorId,
        code: mfaCode.trim(),
      });
      if (error) {
        setErrorMsg(error.message);
        return;
      }
      navigate("/");
    } catch (error) {
      setErrorMsg(error?.message ?? String(error));
    } finally {
      setLoading(false);
    }
  };

  // Abandon de l'étape MFA : on ferme la session aal1 pour revenir au formulaire email / mot de passe.
  const cancelMfa = async () => {
    await signOut();
    setFactors([]);
    setSelectedFactorId(null);
    setMfaCode("");
    setErrorMsg("");
  };

  const selectedIndex = factors.findIndex((f) => f.id === selectedFactorId);
  const selectedFactor = selectedIndex >= 0 ? factors[selectedIndex] : null;

  return (
    <div className="bgimage">
    <AppNavBar />
    <Container className="d-flex align-items-center justify-content-center" style={{minHeight: "90vh"}}>
    <div className="w-100" style={{ maxWidth: "400px"  }}>
      <Card>
        <Card.Body>
          {factors.length > 0 ? (
          <>
          <h2 className="text-center mb-4">Double authentification</h2>
          <Form onSubmit={handleMfaSubmit}>
            {factors.length > 1 && (
              <Form.Group id="mfaFactor" className="mb-3">
                <Form.Label>Choisissez le facteur à utiliser</Form.Label>
                {factors.map((factor, index) => (
                  <Form.Check
                    key={factor.id}
                    type="radio"
                    id={`factor-${factor.id}`}
                    name="mfaFactor"
                    label={factorLabel(factor, index)}
                    checked={factor.id === selectedFactorId}
                    onChange={() => { setSelectedFactorId(factor.id); setErrorMsg(""); }}
                  />
                ))}
              </Form.Group>
            )}
            {selectedFactor && (
              <p className="text-center">
                Saisissez le code de l'entrée « {authenticatorEntry(selectedFactor)} » de votre application d'authentification.
              </p>
            )}
            <Form.Group id="mfaCode">
              <Form.Label>Code MFA de vérification</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                autoFocus
                required
              />
            </Form.Group>
            {errorMsg && (
              <Alert
                variant="danger"
                onClose={() => setErrorMsg("")}
                dismissible>
                {errorMsg}
              </Alert>
            )}
            <div className="text-center mt-2">
              <Button disabled={loading || !selectedFactorId || !mfaCode.trim()} type="submit" className="w-50">
                Vérifier
              </Button>
            </div>
            <div className="text-center mt-2">
              <Button variant="link" onClick={cancelMfa} disabled={loading}>
                Annuler
              </Button>
            </div>
          </Form>
          </>
          ) : (
          <>
            <h2 className="text-center mb-4">Login</h2>
            <Form onSubmit={handleSubmit}>
              <Form.Group id="email">
                <Form.Label>Email</Form.Label>
                <Form.Control type="email" ref={emailRef} required />
              </Form.Group>
              <Form.Group id="password">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" ref={passwordRef} required />
              </Form.Group>
              {errorMsg && (
                <Alert
                  variant="danger"
                  onClose={() => setErrorMsg("")}
                  dismissible>
                  {errorMsg}
                </Alert>
              )}
              <div className="text-center mt-2">
                <Button disabled={loading} type="submit" className="w-50">
                  Login
                </Button>
              </div>
            </Form>
          </>
          )}
        </Card.Body>
        <div className="w-100 text-center mt-2">
          New User? <Link to={"/signup"}>Register</Link>
        </div>
      </Card>
    </div>
    </Container>
    </div>

  );
};

export default Login;